import { useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { useCreateChecklistSerialMutation } from "app/api/checklists";
import { ProductRow } from "models/Order";
import { useChecklistContext } from "context/ChecklistContext";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import LargeDialog from "components/LargeDialog";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";

type Props = {
  onClose?: () => void;
  parentId?: string;
  productRow: ProductRow;
};

const CreateOrderSerialDialog: React.FC<Props> = ({ onClose, parentId, productRow }) => {
  const { checklist } = useChecklistContext();
  const [createSerial, { isLoading }] = useCreateChecklistSerialMutation();

  // Variables for new serial form
  const [serialNumber, setSerialNumber] = useState<string>("");
  const [macAddressWired, setMacAddressWired] = useState<string>("");
  const [ethernetPort, setEthernetPort] = useState<string>("");
  const [theftMarking, setTheftMarking] = useState<string>("");
  const [macAddressWifi, setMacAddressWifi] = useState<string>("");
  const [ipAddress, setIpAddress] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [assetNumber, setAssetNumber] = useState<string>("");
  const [firmware, setFirmware] = useState<string>("");
  const [other, setOther] = useState<string>("");
  const [doa, setDoa] = useState<boolean>(false);

  const isNoFieldEmpty = Boolean(
    macAddressWired &&
    ethernetPort &&
    theftMarking &&
    macAddressWifi &&
    ipAddress &&
    username &&
    password &&
    assetNumber &&
    firmware &&
    other
  );

  const [showAllFields, setShowAllFields] = useState<boolean>(false);

  const handleAddSerial = async () => {
    createSerial({
      checklistId: checklist.id,
      orderId: checklist.partOfOrder,
      rowNo: productRow?.rowId ?? 0,
      parentId: parentId || null,
      serial: serialNumber,
      macAddressWired: macAddressWired,
      macAddressWifi: macAddressWifi,
      ethernetPort: ethernetPort,
      theftMarking: theftMarking,
      ip: ipAddress,
      userName: username,
      password: password,
      firmware: firmware,
      assetNo: assetNumber,
      other: other,
      doa: doa,
    })
      .unwrap()
      .then(() => {
        toast.success("Produkten har lagts till!");
        onClose?.();
      })
      .catch(() => toast.error("Något gick fel när produkten skulle läggas till."));
  };

  return (
    <LargeDialog
      title="Skapa nytt serienummer"
      onClose={() => onClose?.()}
      onSubmit={(e) => {
        e.preventDefault();
        handleAddSerial();
      }}
      actions={
        <>
          <Button
            variant="outlined"
            disabled={isLoading}
            startIcon={<CloseIcon />}
            onClick={() => onClose?.()}
          >
            Avbryt
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="success"
            startIcon={<SaveIcon />}
            loading={isLoading}
          >
            Spara
          </Button>
        </>
      }
    >
      <Box>
        <Typography fontWeight="bold">{productRow.item}</Typography>
        <Typography>{productRow.description}</Typography>
      </Box>

      <TextField
        variant="filled"
        label="Serienummer"
        fullWidth
        required
        value={serialNumber}
        onChange={(e) => setSerialNumber(e.target.value)}
      />
      <TextField
        variant="filled"
        label="MAC-adress"
        fullWidth
        hidden={!showAllFields && !macAddressWired}
        value={macAddressWired}
        onChange={(e) => setMacAddressWired(e.target.value)}
      />
      <TextField
        variant="filled"
        label="MAC-adress Wi-Fi"
        fullWidth
        hidden={!showAllFields && !macAddressWifi}
        value={macAddressWifi}
        onChange={(e) => setMacAddressWifi(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Nätverksuttag"
        fullWidth
        hidden={!showAllFields && !ethernetPort}
        value={ethernetPort}
        onChange={(e) => setEthernetPort(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Stöldmärkning"
        fullWidth
        hidden={!showAllFields && !theftMarking}
        value={theftMarking}
        onChange={(e) => setTheftMarking(e.target.value)}
      />
      <TextField
        variant="filled"
        label="IP-adress (om fast)"
        fullWidth
        hidden={!showAllFields && !ipAddress}
        value={ipAddress}
        onChange={(e) => setIpAddress(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Användarnamn"
        fullWidth
        hidden={!showAllFields && !username}
        value={username}
        onChange={(e) => setUsername(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Lösenord"
        fullWidth
        hidden={!showAllFields && !password}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Asset-nummer"
        fullWidth
        hidden={!showAllFields && !assetNumber}
        value={assetNumber}
        onChange={(e) => setAssetNumber(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Firmware-version"
        fullWidth
        hidden={!showAllFields && !firmware}
        value={firmware}
        onChange={(e) => setFirmware(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Övrigt"
        fullWidth
        hidden={!showAllFields && !other}
        value={other}
        onChange={(e) => setOther(e.target.value)}
      />

      <FormControlLabel
        label="DOA"
        labelPlacement="start"
        sx={{ ml: 0 }}
        control={
          <Checkbox
            checked={doa}
            onChange={(e) => {
              setDoa(e.target.checked);
            }}
          />
        }
      />

      <Button
        hidden={isNoFieldEmpty}
        startIcon={showAllFields ? <ExpandLess /> : <ExpandMore />}
        onClick={() => setShowAllFields(!showAllFields)}
        sx={{ mt: -1, mb: -2 }}
      >
        {showAllFields ? "Dölj tomma fält" : "Visa fler fält"}
      </Button>
    </LargeDialog>
  );
};

export default CreateOrderSerialDialog;
